import type { AnswerDetailResponse } from '@coorum-research/shared-common/api-response'
import type { CampaignSimpleResponse } from '@coorum-research/shared-common/api-response/popup.schema'
import type { SurveyAnswerRecord } from '@coorum-research/shared-common/types/answer.type'
import type { MutableRefObject } from 'react'
import { Modal } from '@coorum-research/core/src/components/Modal'
import { ThemeButton } from '@coorum-research/core/src/components/ThemeButton'
import { ThemeContext } from '@coorum-research/core/src/components/ThemeContext'
import { coorumColors } from '@coorum-research/core/src/survey/coorumTheme'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import { useAnswerFlowUpdate } from '../composables/use-answer-flow-update'
import { getSurveyWithConfigQueryOptions } from '../fetch/survey.fetch'
import { SurveyComponentFlowWithState } from './SurveyComponentFlowWithState'

type Props = {
  answer: AnswerDetailResponse
  campaign: CampaignSimpleResponse
  surveyId: string
  onComplete: (nextLaunchContentId: string | null) => void
  containerRef?: MutableRefObject<HTMLDivElement | null>
  isInterrupt?: boolean
  setIsInterrupt?: (v: boolean) => void
  onClose?: () => void
}

/**
 * アンケート回答の修正画面
 */
export function SurveyAppAnswerUpdate({
  answer,
  campaign,
  surveyId,
  onComplete,
  containerRef,
  isInterrupt,
  setIsInterrupt,
  onClose,
}: Props) {
  const { data: survey, isFetching } = useQuery(
    getSurveyWithConfigQueryOptions(surveyId),
  )

  const {
    onCompleteQuestion,
    onUploadFiles,
    onCompleteSurvey,
    getCurrentSurveyData,
    setCurrentSurveyData,
    getCurrentSurveyCompleteDisabled,
  } = useAnswerFlowUpdate({
    campaignId: campaign.id,
    surveyId,
    surveyConfigurationId: survey?.surveyConfigurationId ?? '',
    onComplete,
  })

  useEffect(() => {
    if (answer?.surveyAnswer) {
      try {
        const surveyAnswerData = JSON.parse(answer.surveyAnswer)
        setCurrentSurveyData(surveyAnswerData)
      } catch (e) {
        console.error('Failed to parse survey answer:', e)
      }
    }
  }, [answer?.surveyAnswer, setCurrentSurveyData])

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose()
    }
    // reset temp data
    setCurrentSurveyData(null)
  }, [onClose, setCurrentSurveyData])

  const handleCompleteSurvey = useCallback(async (nextLaunchContentId: string | null, senderData: SurveyAnswerRecord | null) => {
    await onCompleteSurvey(answer.id, nextLaunchContentId, senderData)
  }, [answer.id, onCompleteSurvey])

  if (isFetching || !survey) {
    return <></>
  }

  return (
    <div className="relative h-full min-w-full">
      <SurveyComponentFlowWithState
        surveyConfig={survey.configJson ?? null}
        themeJson={survey.themeConfigJson}
        campaignLogicJson={campaign.campaignLogicJson ?? null}
        onCompleteQuestion={onCompleteQuestion}
        onComplete={handleCompleteSurvey}
        onUploadFiles={onUploadFiles}
        getCurrentSurveyData={getCurrentSurveyData}
        campaignDefaultLauncherContentId={campaign.launcherContentId}
        getCurrentSurveyCompleteDisabled={getCurrentSurveyCompleteDisabled}
      />
      <ThemeContext value={{ ...coorumColors, ...survey.themeConfigJson }}>
        <Modal.Root open={isInterrupt} onOpenChange={setIsInterrupt}>
          <Modal.Portal container={containerRef?.current}>
            <Modal.Overlay />
            <Modal.Content className="w-[350px] bg-white">
              <Modal.Title className="mt-1">
                中断しますか？
              </Modal.Title>
              <Modal.Description>
                回答いただいた内容は保存されません。
              </Modal.Description>
              <div className="flex gap-2">
                <Modal.Close asChild>
                  <ThemeButton
                    variant="outline"
                    className="min-w-[120px] !px-2 text-sm font-bold"
                  >
                    キャンセル
                  </ThemeButton>
                </Modal.Close>
                <ThemeButton
                  className="!px-2 text-sm font-bold"
                  onClick={handleClose}
                >
                  アンケートを中断
                </ThemeButton>
              </div>
            </Modal.Content>
          </Modal.Portal>
        </Modal.Root>
      </ThemeContext>
    </div>
  )
}
